import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { TrendingUp, Percent, DollarSign, PiggyBank, Calculator, Building, Building2, AlertTriangle } from "lucide-react";
import { PeriodSelector, KPICard, IndicatorRow } from "@/components/UIComponents";
import { TrendModal } from "@/components/TrendModal";
import { formatCurrency, formatPercent, MONTHS, TARGETS, CONFIG } from "@/lib/config";
import TARGETS_CONFIG, { getStatusColor } from "@/lib/targets";
import api from "@/lib/api";

export const Rentabilite = () => {
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(1);
  const [quarter, setQuarter] = useState(1);
  const [view, setView] = useState("mensuel");
  const [selectedDept, setSelectedDept] = useState("GLOBAL");
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [ratios, setRatios] = useState(null);
  const [previousData, setPreviousData] = useState(null);
  
  // Modal de tendance
  const [showTrendModal, setShowTrendModal] = useState(false);
  const [selectedKPI, setSelectedKPI] = useState(null);
  
  // Charger les cibles depuis MongoDB
  const [targets, setTargets] = useState(TARGETS_CONFIG);
  useEffect(() => {
    axios.get(`${window.__API_URL__ || ""}/api/configuration/targets`)
      .then(r => r.data)
      .then(d => { if (d.targets) setTargets(d.targets); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { getMonthsForPeriod, aggregateMonthlyData } = await import('@/lib/periodUtils');
        const periods = getMonthsForPeriod(view, year, month, quarter);
        
        // Récupérer les données pour chaque période
        const monthsData = await Promise.all(
          periods.map(p => api.getDashboard(p.year, p.month))
        );
        
        // Agréger les données
        const dashboard = aggregateMonthlyData(monthsData);
        setDashboardData(dashboard);
        
        // Pour l'instant, ratios en mensuel seulement
        if (view === "mensuel") {
          const ratiosData = await api.getRatios(year, month);
          setRatios(ratiosData);
        }
      } catch (error) {
        console.error("Error:", error);
      }
      setLoading(false);
    };
    fetchData();
  }, [year, month, quarter, view]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div></div>;
  }

  const globalKpi = dashboardData?.kpis?.GLOBAL || {};
  const profits = globalKpi.profits || {};
  const marges = globalKpi.marges || {};
  const revenus = globalKpi.revenus || {};
  const depenses = globalKpi.depenses || {};
  const rentabilite = ratios?.rentabilite || {};

  // Données précédentes pour les tendances
  const prevGlobalKpi = previousData?.kpis?.GLOBAL || {};
  const prevMarges = prevGlobalKpi.marges || {};

  // Calculs supplémentaires — basés sur les vraies données
  const fraisFixes = depenses.net || 0; // Frais fixes = dépenses niveau "Net" (loyer, assurances, telecom, SaaS)
  const fraisFixesPct = revenus.total > 0 ? (fraisFixes / revenus.total * 100) : 0;
  
  // Dividendes: pas encore trackés dans la plateforme — afficher 0 par défaut
  const dividendes = 0;
  const resultatNonDistribue = profits.apres_impots - dividendes;
  const resultatNonDistribuePct = revenus.total > 0 ? (resultatNonDistribue / revenus.total * 100) : 0;

  const noData = (revenus.total || 0) === 0 && (depenses.total || 0) === 0;

  // Accès aux configurations de cibles
  const rentaTargets = targets.rentabilite || {};

  return (
    <div data-testid="rentabilite-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between animate-fade-in">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Rentabilité & Performance</h1>
          <p className="text-slate-500 mt-1 text-sm">{MONTHS[month]} {year}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={selectedDept} onValueChange={setSelectedDept}>
            <SelectTrigger className="w-full sm:w-48 bg-white">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="GLOBAL">📊 Tous départements</SelectItem>
              <SelectItem value="PRQC">🎨 {CONFIG.department_names.PRQC}</SelectItem>
              <SelectItem value="LMDS">🏪 {CONFIG.department_names.LMDS}</SelectItem>
              <SelectItem value="LOCATION">🚚 {CONFIG.department_names.LOCATION}</SelectItem>
            </SelectContent>
          </Select>
          <PeriodSelector 
            year={year} 
            setYear={setYear} 
            month={month} 
            setMonth={setMonth} 
            view={view}
            setView={setView}
            quarter={quarter}
            setQuarter={setQuarter}
          />
        </div>
      </div>

      {noData && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">Aucune donnée pour {MONTHS[month]} {year}</p>
            <p className="text-xs text-amber-600 mt-0.5">Sélectionnez une période avec des transactions QuickBooks importées (2024 ou 2025).</p>
          </div>
        </div>
      )}

      {/* KPIs principaux */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in delay-100">
        <KPICard
          title="Marge Brute"
          value={formatPercent(marges.brute)}
          subtitle={`Profit brut: ${formatCurrency(profits.brut)}`}
          icon={TrendingUp}
          color="emerald"
          targetConfig={rentaTargets.marge_brute}
          previousValue={prevMarges.brute}
          description={rentaTargets.marge_brute?.description}
          onClick={() => {
            setSelectedKPI({title: "Marge Brute", kpiKey: "marge_brute", isPercent: true, departement: selectedDept});
            setShowTrendModal(true);
          }}
          kpiKey="marge_brute"
        />
        <KPICard
          title="Frais fixes % du CA"
          value={formatPercent(fraisFixesPct)}
          subtitle={`Frais fixes: ${formatCurrency(fraisFixes)}`}
          icon={Building}
          color="blue"
          targetConfig={rentaTargets.frais_fixes_pct}
          description={rentaTargets.frais_fixes_pct?.description}
          onClick={() => {
            setSelectedKPI({title: "Frais Fixes % CA", kpiKey: "depenses", isPercent: false, departement: selectedDept});
            setShowTrendModal(true);
          }}
          kpiKey="depenses"
        />
        <KPICard
          title="Marge nette avant impôts"
          value={formatPercent(marges.nette)}
          subtitle={`Profit: ${formatCurrency(profits.avant_impots)}`}
          icon={Calculator}
          color="amber"
          targetConfig={rentaTargets.marge_nette_avant_impots}
          previousValue={prevMarges.nette}
          description={rentaTargets.marge_nette_avant_impots?.description}
          onClick={() => {
            setSelectedKPI({title: "Marge Nette Avant Impôts", kpiKey: "marge_nette", isPercent: true, departement: selectedDept});
            setShowTrendModal(true);
          }}
          kpiKey="marge_nette"
        />
        <KPICard
          title="Profit Net"
          value={formatCurrency(profits.apres_impots)}
          subtitle={`Marge après impôts: ${formatPercent(marges.nette)}`}
          icon={PiggyBank}
          color="emerald"
          targetConfig={rentaTargets.marge_nette_apres_impots}
          description={rentaTargets.marge_nette_apres_impots?.description}
          onClick={() => {
            setSelectedKPI({title: "Profit Net", kpiKey: "profit", isPercent: false, departement: selectedDept});
            setShowTrendModal(true);
          }}
          kpiKey="profit"
        />
      </div>

      {/* Détails rentabilité */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in delay-200">
        {/* Cascade de rentabilité */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Cascade de Rentabilité</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Revenus totaux</span>
                <span className="money-value font-bold text-emerald-600">{formatCurrency(revenus.total)}</span>
              </div>
              <div className="flex justify-between items-center text-red-500">
                <span>- Coûts directs (terrain, matériaux)</span>
                <span className="money-value">-{formatCurrency(depenses.brut || 0)}</span>
              </div>
              <div className="border-t border-slate-200 pt-2 flex justify-between items-center">
                <span className="font-medium">= Profit Brut</span>
                <span className="money-value font-bold text-slate-900">{formatCurrency(profits.brut)}</span>
              </div>
              <div className="flex justify-between items-center text-red-500">
                <span>- Charges indirectes (marketing, etc.)</span>
                <span className="money-value">-{formatCurrency(depenses.marge || 0)}</span>
              </div>
              <div className="border-t border-slate-200 pt-2 flex justify-between items-center">
                <span className="font-medium">= Marge contributive</span>
                <span className="money-value font-bold text-slate-900">{formatCurrency(profits.contributif || (profits.brut - (depenses.marge || 0)))}</span>
              </div>
              <div className="flex justify-between items-center text-red-500">
                <span>- Frais fixes (loyer, assurances, SaaS)</span>
                <span className="money-value">-{formatCurrency(fraisFixes)}</span>
              </div>
              <div className="border-t border-slate-200 pt-2 flex justify-between items-center">
                <span className="font-medium">= Profit Avant Impôts</span>
                <span className="money-value font-bold text-slate-900">{formatCurrency(profits.avant_impots)}</span>
              </div>
              <div className="flex justify-between items-center text-amber-600">
                <span>- Impôts ({profits.avant_impots > 0 ? '15' : '0'}%)</span>
                <span className="money-value">-{formatCurrency(profits.impots)}</span>
              </div>
              <div className="border-t-2 border-emerald-500 pt-2 flex justify-between items-center">
                <span className="font-bold text-emerald-900">= Profit Net</span>
                <span className="money-value font-bold text-emerald-600 text-xl">{formatCurrency(profits.apres_impots)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Indicateurs de rentabilité */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Indicateurs de Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              <IndicatorRow 
                label="Marge Brute" 
                value={marges.brute?.toFixed(1)} 
                unit="%" 
                targetConfig={rentaTargets.marge_brute}
                description={rentaTargets.marge_brute?.description}
              />
              <IndicatorRow 
                label="Frais fixes / Revenus" 
                value={fraisFixesPct.toFixed(1)} 
                unit="%" 
                targetConfig={rentaTargets.frais_fixes_pct}
                description={rentaTargets.frais_fixes_pct?.description}
              />
              <IndicatorRow 
                label="Marge Nette avant impôts" 
                value={marges.nette?.toFixed(1)} 
                unit="%" 
                targetConfig={rentaTargets.marge_nette_avant_impots}
                description={rentaTargets.marge_nette_avant_impots?.description}
              />
              <IndicatorRow 
                label="Marge Nette après impôts" 
                value={marges.nette?.toFixed(1)} 
                unit="%" 
                targetConfig={rentaTargets.marge_nette_apres_impots}
                description={rentaTargets.marge_nette_apres_impots?.description}
              />
              <IndicatorRow 
                label="ROA (mensuel)" 
                value={rentabilite.roi?.toFixed(1)} 
                unit="%" 
                targetConfig={rentaTargets.roa_mensuel}
                description={rentaTargets.roa_mensuel?.description}
              />
              <div className="border-t border-slate-200 mt-4 pt-4">
                <IndicatorRow 
                  label="Résultat non distribué (YTD)" 
                  value={resultatNonDistribuePct.toFixed(1)} 
                  unit="%" 
                  targetConfig={rentaTargets.resultat_non_distribue}
                  description={rentaTargets.resultat_non_distribue?.description}
                />
                <IndicatorRow label="Dividendes versés" value={formatCurrency(dividendes)} />
                <IndicatorRow label="Montant non distribué" value={formatCurrency(resultatNonDistribue)} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Rentabilité par département */}
      <Card className="animate-fade-in delay-300">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Rentabilité par Département</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {CONFIG.departments.map(dept => {
              const deptKpi = dashboardData?.kpis?.[dept] || {};
              const deptProfits = deptKpi.profits || {};
              const deptMarges = deptKpi.marges || {};
              
              return (
                <div key={dept} className="bg-slate-50 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium text-slate-900">{CONFIG.department_names[dept]}</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${deptMarges.nette >= 15 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {formatPercent(deptMarges.nette)} marge
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Profit brut</span>
                      <span className="money-value text-slate-900">{formatCurrency(deptProfits.brut)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Profit net</span>
                      <span className={`money-value ${deptProfits.apres_impots >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {formatCurrency(deptProfits.apres_impots)}
                      </span>
                    </div>
                    <Progress value={Math.max(0, deptMarges.brute)} className="h-2 mt-2" />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Modal Tendance */}
      {showTrendModal && selectedKPI && (
        <TrendModal
          open={showTrendModal}
          onClose={() => setShowTrendModal(false)}
          title={selectedKPI.title}
          kpiKey={selectedKPI.kpiKey}
          departement={selectedKPI.departement}
          isPercent={selectedKPI.isPercent}
          chartType={selectedKPI.isPercent ? "line" : "bar"}
        />
      )}
    </div>
  );
};

export default Rentabilite;
